import React, { useState } from 'react';
import { applySevagramCreator } from '../../lib/api';

const initial = {
  handle: '',
  display_name: '',
  email: '',
  instagram: '',
  tiktok: '',
  payout_address: '',
  bio: '',
};

const ApplyCreatorForm = () => {
  const [form, setForm] = useState(initial);
  const [state, setState] = useState({ loading: false, ok: null, err: '' });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setState({ loading: true, ok: null, err: '' });
    try {
      const res = await applySevagramCreator(form);
      setState({ loading: false, ok: res, err: '' });
      setForm(initial);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Could not submit. Please try again.';
      setState({
        loading: false,
        ok: null,
        err: typeof detail === 'string' ? detail : 'Please check your entries.',
      });
    }
  };

  if (state.ok) {
    return (
      <div
        data-testid="apply-success"
        className="border border-[#F4F8F9]/40 p-8 bg-[#F4F8F9]/10 backdrop-blur-md"
      >
        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F8F9]/80 mb-3">
          Application received
        </p>
        <h3 className="font-serif text-3xl font-light mb-4">Welcome, {state.ok.display_name}.</h3>
        <p className="text-[#F4F8F9]/90 font-light leading-relaxed mb-6">
          Your status is <em>{state.ok.status}</em>. As soon as we approve your
          application, your ref code becomes active across the feed.
        </p>
        <div className="font-mono text-sm bg-[#F4F8F9]/10 border border-[#F4F8F9]/30 px-4 py-3 inline-flex items-center gap-3">
          <span className="text-[#F4F8F9]/70 tracking-[0.25em] uppercase text-[10px]">
            Ref
          </span>
          <span data-testid="apply-success-ref">{state.ok.ref_code}</span>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="apply-form"
      className="space-y-5 bg-[#F4F8F9]/10 backdrop-blur-md p-8 border border-[#F4F8F9]/30"
    >
      <Field label="Handle (no @)">
        <input
          required
          minLength={2}
          maxLength={40}
          value={form.handle}
          onChange={update('handle')}
          placeholder="maya-k"
          data-testid="apply-handle-input"
          className="apply-input"
        />
      </Field>
      <Field label="Display name">
        <input
          required
          value={form.display_name}
          onChange={update('display_name')}
          data-testid="apply-name-input"
          className="apply-input"
        />
      </Field>
      <Field label="Email">
        <input
          type="email"
          required
          value={form.email}
          onChange={update('email')}
          data-testid="apply-email-input"
          className="apply-input"
        />
      </Field>
      <div className="grid grid-cols-2 gap-5">
        <Field label="Instagram">
          <input
            value={form.instagram}
            onChange={update('instagram')}
            placeholder="@yourhandle"
            data-testid="apply-instagram-input"
            className="apply-input"
          />
        </Field>
        <Field label="TikTok">
          <input
            value={form.tiktok}
            onChange={update('tiktok')}
            placeholder="@yourhandle"
            data-testid="apply-tiktok-input"
            className="apply-input"
          />
        </Field>
      </div>
      <Field label="Payout address (wallet, optional)">
        <input
          value={form.payout_address}
          onChange={update('payout_address')}
          placeholder="0x… or bank reference"
          data-testid="apply-payout-input"
          className="apply-input"
        />
      </Field>
      <Field label="A few words about your ritual">
        <textarea
          rows={3}
          value={form.bio}
          onChange={update('bio')}
          data-testid="apply-bio-input"
          className="apply-input resize-none"
        />
      </Field>
      <button
        type="submit"
        disabled={state.loading}
        data-testid="apply-submit"
        className="bg-[#F4F8F9] text-[#7C9CB1] px-8 py-4 text-[11px] tracking-[0.22em] uppercase hover:bg-[#C1D7E2] transition-colors disabled:opacity-60"
      >
        {state.loading ? 'Sending…' : 'Submit application'}
      </button>
      {state.err && (
        <p data-testid="apply-error" className="text-sm text-[#F4F8F9]/90">
          {state.err}
        </p>
      )}
      <style>{`
        .apply-input {
          width: 100%;
          background: transparent;
          border: 0;
          border-bottom: 1px solid rgba(244,248,249,0.4);
          color: #F4F8F9;
          font-family: 'Outfit', sans-serif;
          font-weight: 300;
          padding: 10px 2px;
          font-size: 15px;
        }
        .apply-input::placeholder { color: rgba(244,248,249,0.45); }
        .apply-input:focus { outline: none; border-bottom-color: #F4F8F9; }
      `}</style>
    </form>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F8F9]/80">
      {label}
    </span>
    <div className="mt-2">{children}</div>
  </label>
);

export default ApplyCreatorForm;
